import React, { useState, useEffect } from "react";
import { FormikValues } from "formik";
import { FileUpload, Visibility, Delete } from "@mui/icons-material";
import { PdfViewerModalV2 } from "../molecules/PdfViewerModal";
import { useUploadPptMockMutation } from "../../api/fileUpload";
import { CreativeType } from "../../types/types";

interface PptFieldProps {
  name: string;
  label: string;
  formik: FormikValues;
  width?: "full" | "half";
  setPpt: Function;
  setPptUrl: Function;
}

export const PptField: React.FC<PptFieldProps> = ({
  name,
  label,
  formik,
  width = "full",
  setPpt,
  setPptUrl,
}) => {
  console.log("🚀 ~ PptField ~ formik:", formik?.values?.pptDetails?.pdfLink)
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    formik.values?.pptDetails?.pdfLink || null
  );


  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [uploadPpt, { isLoading: uploadIsLoading }] = useUploadPptMockMutation();

  // Debug effect to monitor modal state
  useEffect(() => {
    if (formik.values?.pptDetails?.pdfLink) {
      setPreviewUrl(formik.values?.pptDetails?.pdfLink);
    }
  }, [formik]);



  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check if file is PowerPoint
      if (!file.type.includes("powerpoint") && !file.type.includes("presentation") && !file.name.toLowerCase().endsWith('.ppt') && !file.name.toLowerCase().endsWith('.pptx')) {
        alert("Please select a PowerPoint file (.ppt or .pptx)");
        return;
      }

      // Create FormData for S3 upload
      const formData = new FormData();
      const fileName = (formik.values.fileName && formik.values.fileName !== "") ? formik.values.fileName : file.name
      formData.append("ppt", file);
      formData.append("fileName", fileName.split(".")[0]);

      // Upload to S3
      const res = await uploadPpt(formData)
        .unwrap()
        .then((res) => {
          if (res.data.data.s3PptUrl && res.data.data.s3Url) {
            setPreviewUrl(res.data.data.s3Url);
            formik.setFieldValue('pptDetails.pptLink', res.data.data.s3PptUrl);
            formik.setFieldValue('pptDetails.pdfLink', res.data.data.s3Url);
          }
        })
        .catch((error) => {
          console.log("PPT upload error:", error);
          alert("Failed to upload PowerPoint file. Please try again.");
        });
    }
  };

  const handleRemoveFile = () => {
    setPreviewUrl(null);
    formik.setFieldValue('pptDetails.pptLink', "");
    formik.setFieldValue('pptDetails.pdfLink', "");
    setPpt(false);
    setPptUrl("");
  };

  const handlePreview = () => {
    console.log("handlePreview called");
    console.log("previewUrl:", previewUrl);
    console.log("isPdfModalOpen before:", isPdfModalOpen);

    if (previewUrl) {
      console.log("Setting modal to open");
      setIsPdfModalOpen(true);
      console.log("Modal should be open now");
    } else {
      console.log("No preview URL available");
    }
  };

  return (
    <div className={`${width === "half" ? "w-[48%]" : "w-full"} mb-4`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>

      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
        {!formik.values?.pptDetails?.pdfLink ? (
          <div className="text-center">
            <FileUpload className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-2">
              <label
                htmlFor={`ppt-${name}`}
                className={`cursor-pointer px-4 py-2 rounded-md transition-colors ${uploadIsLoading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
              >
                {uploadIsLoading ? 'Uploading...' : 'Upload PowerPoint'}
              </label>
              <input
                id={`ppt-${name}`}
                type="file"
                accept=".ppt,.pptx"
                onChange={handleFileChange}
                className="hidden"
                disabled={uploadIsLoading}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Only PowerPoint files (.ppt, .pptx) are allowed
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded">
                <FileUpload className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  PowerPoint uploaded successfully
                </p>
                <p className="text-xs text-gray-500">
                  Click preview to view the PPT version
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={handlePreview}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title="Preview PPT"
              >
                <Visibility className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title="Remove PPT"
              >
                <Delete className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {formik.touched[name] && formik.errors[name] && (
        <p className="text-red-500 text-xs mt-1">{formik.errors[name]}</p>
      )}

      {/* PPT Viewer Modal */}
      {isPdfModalOpen && previewUrl && formik.values?.pptDetails?.pdfLink && formik.values?.pptDetails?.pdfLink.trim() !== "" && (
        <PdfViewerModalV2
          open={isPdfModalOpen}
          onClose={() => setIsPdfModalOpen(false)}
          pdfLink={formik.values.pptDetails.pdfLink}
          pptLink={formik.values.pptDetails.pptLink}
        />
      )}
    </div>
  );
};
